import { useCallback, useMemo } from 'react'
import { Typography, Stack } from '@mui/material'
import { i18WithParams as t } from '@shared/locales'
import { ReceiptAdminDto, ReceiptClientDto } from 'service-api/src/types'
import { Spacer1 } from 'spacer'
import { formatNumberAsPrice } from '@shared/common'

export const Footer = ({
  receipt,
  dueToday,
}: {
  receipt: ReceiptClientDto | ReceiptAdminDto
  dueToday?: number | null
}) => {
  const dueTodayPercentage = useMemo(() => {
    if (!receipt.total || !dueToday) return ''
    const totalVal = parseFloat(receipt.total.substring(1))
    const percent = totalVal ? Math.round((dueToday / totalVal) * 100) : 0
    return `${percent}%`
  }, [dueToday, receipt.total])

  const renderDueAndPaidInfo = useCallback(() => {
    switch (dueToday) {
      case null: {
        // if dueToday is passed but is null, the receipt is in a pre-confirmation step
        return null
      }
      case undefined: {
        // if undefined (never passed), the receipt is in a confirmation step
        return (
          <Stack direction="row" justifyContent="space-between" mt={3}>
            <Typography variant="h5">{t('global.label.paidAtBooking')}</Typography>
            <Typography variant="h5">{receipt.paid}</Typography>
          </Stack>
        )
      }
      default: {
        return (
          <Stack direction="row" justifyContent="space-between" mt={3}>
            <Typography variant="h5">{t('global.label.dueToday', { percentage: dueTodayPercentage })}</Typography>
            <Typography variant="h5">{formatNumberAsPrice(dueToday)}</Typography>
          </Stack>
        )
      }
    }
  }, [dueToday, receipt.paid, dueTodayPercentage])
  return (
    <>
      <Stack direction="row" justifyContent="space-between" mt={3}>
        <Typography variant="h5">{t('global.label.total')}</Typography>
        <Typography variant="h5">{receipt.total}</Typography>
      </Stack>
      {renderDueAndPaidInfo()}
      {receipt.refunds?.length && (
        <Stack mt={3}>
          <Typography variant="h5">{t('global.label.adjustments')}</Typography>
          <Spacer1 />
          {receipt.refunds.map(({ displayName, amount }, idx) => (
            <Stack key={`${displayName}-${idx}`} direction="row" justifyContent="space-between" mt={1} ml={1} mr={-1}>
              <Typography variant="body1">{displayName}</Typography>
              <Typography variant="body1">{amount}</Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </>
  )
}
